import {
  GetOption,
  HateoasResourceOperation,
  HateoasResourceService,
  Resource
} from "@lagoshny/ngx-hateoas-client";
import { BehaviorSubject, firstValueFrom, Observable } from "rxjs";
import { EntityChangeType } from "@app/services/webstomp/entity-change-event";
import { Injectable } from "@angular/core";
import { StompRouterService } from "@app/services/webstomp/stomp-router.service";

@Injectable()
export class RealTimeStreamingStore<T extends Resource> {
  protected dataSet: T[] = [];

  /* eslint-disable @angular-eslint/prefer-inject -- Abstract base class requires constructor params for super() calls from subclasses */
  constructor(
    protected restService: HateoasResourceOperation<T>,
    protected stompRouterService: StompRouterService,
    protected hateoasResourceService: HateoasResourceService
  ) {
    /* eslint-enable @angular-eslint/prefer-inject */
    this._updateStream = new BehaviorSubject<T>(null);
  }

  protected _updateStream: BehaviorSubject<T>;
  get updateStream(): Observable<T> {
    return this._updateStream.asObservable();
  }

  loadAll(projection: string = null): Promise<any> {
    let halOptions: GetOption = null;
    if (projection) {
      halOptions = { params: { projection: projection } };
    }
    return new Promise((resolve, reject) => {
      this.restService.getCollection(halOptions).subscribe(
        (entities) => {
          this.dataSet = entities.resources;
          resolve(entities);
        },
        (error) => reject(error)
      );
    });
  }

  loadWithSearch(
    searchQuery: string,
    options: any,
    detached = false
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.restService.searchCollection(searchQuery, options).subscribe(
        (entities) => {
          if (!detached) {
            this.dataSet = entities.resources;
          }
          resolve(entities);
        },
        (error) => reject(error)
      );
    });
  }

  loadWithSearchPage(
    searchQuery: string,
    options: any,
    detached = false
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.restService.searchPage(searchQuery, options).subscribe(
        (entities) => {
          if (!detached) {
            this.dataSet = entities.resources;
          }
          resolve(entities);
        },
        (error) => reject(error)
      );
    });
  }

  loadSingleProjection<S extends Resource>(
    type: { new (): S },
    id: number | string,
    projection: string
  ): Promise<S> {
    return firstValueFrom(
      this.hateoasResourceService.getResource(type, id, {
        params: { projection: projection }
      })
    );
  }

  loadSingle(id: number): Promise<T> {
    return firstValueFrom(this.restService.getResource(id));
  }

  create(entity: T): Observable<any> {
    return this.restService.createResource({ body: entity });
  }

  patch(entity: T): Observable<any> {
    return this.restService.patchResource(entity);
  }

  update(entity: T): Observable<any> {
    return this.restService.updateResource(entity);
  }

  protected subscribeToMessaging(entityName: string) {
    this.stompRouterService
      .getEntityStream(entityName)
      .subscribe((changeEvent) => {
        // console.log(changeEvent);
        if (changeEvent !== null) {
          switch (changeEvent.action) {
            case EntityChangeType.INSERT:
              this.handleInsert(changeEvent);
              break;
            case EntityChangeType.UPDATE:
              this.handleUpdate(changeEvent);
              break;
          }
        }
      });
  }

  protected handleInsert(changeEvent) {
    this._updateStream.next(changeEvent.payload);
  }

  protected handleUpdate(changeEvent) {
    this._updateStream.next(changeEvent.payload);
    return;
  }
}
